<template>
  <div ref="blockRef">
    <WithBoxSize
      ref="blockRef"
      v-slot="{ borderBoxSize }"
      :class="[$style.config, $style.block]"
    >
      <div
        ref="panAreaRef"
        :class="[
          $style.panArea,
          !isInstructionDismissed && $style.hideOverflow,
          borderBoxSize.width >= computedMinWidth && $style.hideOverflow,
        ]"
      >
        <VueMouseTrack
          v-slot="{ x, y }"
          ref="trackAreaRef"
          :class="$style.trackArea"
          @mousedown="onMouseDown"
          @mouseup="onMouseUp"
          @mousemove="onMouseMove"
        >
          <BaseHeading
            v-if="computedSectionHeader"
            :as="computedSectionHeaderWeight"
            size="h1"
            :class="$style.heading"
            :style="computedSectionHeaderPlacementCoordinate"
          >
            {{ computedSectionHeader }}
          </BaseHeading>
          <AspectRatio
            v-if="computedBackdrop"
            :ratio="`${computedBackdrop?.fields?.file?.details?.image?.width}x${computedBackdrop?.fields?.file?.details?.image?.height}`"
          >
            <picture :class="$style.backdropPicture">
              <source
                :srcset="`${computedBackdrop?.fields?.file?.url}?fm=webp`"
                type="image/webp"
              />
              <img
                :class="$style.backdrop"
                :src="computedBackdrop?.fields?.file?.url"
                :alt="computedBackdrop?.fields?.description"
                loading="lazy"
              />
            </picture>
          </AspectRatio>

          <div
            v-if="showCoordinates"
            :class="$style.position"
            :style="{ left: x, top: y }"
            aria-hidden="true"
          >
            mouse position x: {{ x }}, y: {{ y }}
          </div>

          <template v-if="computedHotspots.length">
            <div
              v-for="hotspot in computedHotspots.filter(instanceOfHotspot)"
              :key="hotspot.sys.id"
              :class="[
                $style.hotspot,
                activeHotspot &&
                  activeHotspot?.sys?.id === hotspot?.sys?.id &&
                  $style.isActive,
              ]"
              :style="getPopoverStyles(hotspot)"
            >
              <BaseButton
                :class="$style.hotspotButton"
                @click.stop="(e: MouseEvent) => onHotSpotClick(e, hotspot)"
              >
                <div
                  v-if="hotspot.fields?.icon"
                  :class="[
                    $style.icon,
                    computedStaticIconOnly && $style.static,
                    computedInvisibleHotspot && $style.hidden,
                  ]"
                >
                  <div :class="$style.iconInner">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="feather feather-plus"
                    >
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </div>
                </div>
                <span v-if="hotspot?.fields?.label" :class="$style.label">
                  {{ hotspot?.fields?.label }}
                </span>
              </BaseButton>
            </div>
          </template>

          <div
            v-if="computedSectionCta.length"
            :class="$style.sectionCta"
            :style="computedSectionCtaPlacementCoordinate"
          >
            <CallToAction :cta="computedSectionCta[0]" />
          </div>
          <div
            v-show="!isInstructionDismissed"
            :class="[
              $style.instruction,
              shouldShowInstruction && $style.isPopperVisible,
            ]"
          >
            <BaseColumn
              cross-axis-alignment="center"
              :class="$style.instructionContent"
            >
              <header :class="$style.instructionHeader">
                <picture v-if="isIntersecting && computedInstructionIcon">
                  <source
                    :srcset="`${computedInstructionIcon?.fields?.file?.url}?fm=webp`"
                    type="image/webp"
                  />
                  <img
                    :class="$style.instructionHeaderIcon"
                    :src="computedInstructionIcon?.fields?.file?.url"
                    alt=""
                    loading="lazy"
                  />
                </picture>
                <h3
                  v-if="computedInstructionHeader"
                  :class="$style.instructionHeading"
                >
                  {{ computedInstructionHeader }}
                </h3>
              </header>

              <MarkedParser
                v-if="computedInstructionIntro"
                :class="$style.instructionIntro"
                :md="computedInstructionIntro"
              />

              <div :class="$style.instructionManual">
                <div
                  v-if="computedHotspotInstructionIcon"
                  :class="[
                    $style.icon,
                    $style.instructionIcon,
                    computedStaticIconOnly && $style.static,
                  ]"
                >
                  <div :class="$style.iconInner">
                    <InlineSvg
                      :src="computedHotspotInstructionIcon"
                      fill="currentColor"
                    />
                  </div>
                </div>
                <MarkedParser
                  v-if="computedInstructionManual"
                  :md="computedInstructionManual"
                />
              </div>

              <div v-if="computedInstructionCta" :class="$style.instructionCta">
                <CallToAction
                  :cta="computedInstructionCta"
                  @click="onRequestDismissInstruction"
                />
              </div>
            </BaseColumn>
          </div>

          <div v-if="isOpen" :class="$style.popup">
            <div ref="detailRef" @click.stop>
              <HotspotCard
                v-if="activeHotspot"
                :hotspot="activeHotspot"
                :border-frame="computedBorderFrame"
                :border-height="computedBorderHeight"
                :is-visible="isPopperVisible"
                @request-close="onRequestDetailClose"
              />
            </div>
          </div>

          <BaseButton
            v-if="shouldRenderCoordinates"
            small
            hollow
            :class="$style.hiddenButton"
            @click="toggleCoordinates"
          >
            coordinates
          </BaseButton>
        </VueMouseTrack>
      </div>
    </WithBoxSize>
  </div>
</template>

<script setup lang="ts">
import type { CSSProperties } from 'vue'
import { createPopper } from '@popperjs/core'
import type { Instance as PopperInstance } from '@popperjs/core'
import InlineSvg from 'vue-inline-svg'

import { loadImage } from '~/utils/imageLoaderUtils'

import type {
  TypeHotspotsMapWithoutUnresolvableLinksResponse,
  TypeHotspotWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

const props = defineProps<{
  data: TypeHotspotsMapWithoutUnresolvableLinksResponse
}>()

const blockRef = ref<HTMLDivElement | null>(null)
const panAreaRef = ref<HTMLDivElement | null>(null)
const trackAreaRef = ref<HTMLDivElement | null>(null)
const detailRef = ref<HTMLDivElement | null>(null)
const isMouseDown = ref(false)
const isOpen = ref(false)
const isPopperVisible = ref(false)
const shouldShowInstruction = ref(false)
const isInstructionDismissed = ref(false)
const showCoordinates = ref(false)
const popperInstance = ref<PopperInstance | null>(null)
const activeHotspot = ref<TypeHotspotWithoutUnresolvableLinksResponse | null>(
  null,
)

const { isIntersecting } = useIntersectionObserver(blockRef, {}, true)

type PlacementCoordinates = {
  x?: number
  y?: number
  offset?: number
}

const computedSectionHeader = computed(() => props.data?.fields?.sectionHeader)
const computedSectionHeaderWeight = computed(
  () => props.data?.fields?.sectionHeaderWeight,
)

const computedSectionHeaderPlacementCoordinate = computed(() => {
  const {
    x = 0,
    y = 0,
    offset,
  } = (props?.data?.fields?.sectionHeaderPlacementCoordinate ||
    {}) as PlacementCoordinates

  return {
    left: x,
    top: y,
    transform: offset ? `transformX(${offset})` : undefined,
  } as CSSProperties
})

const computedSectionCta = computed(() => {
  // it's capped to just one cta for now
  return props.data?.fields?.sectionCta || []
})

const computedSectionCtaPlacementCoordinate = computed(() => {
  const {
    x = 0,
    y = 0,
    offset,
  } = (props?.data?.fields?.sectionCtaPlacementCoordinate ||
    {}) as PlacementCoordinates

  return {
    left: x,
    top: y,
    transform: offset ? `transformX(${offset})` : undefined,
  } as CSSProperties
})

const computedMinWidth = computed(() => {
  return props.data?.fields?.minWidth || 0
})

const computedBackdrop = computed(() => {
  return props.data?.fields?.backdrop
})

const computedStaticIconOnly = computed(() => {
  return props.data?.fields?.staticIconOnly
})

const computedInvisibleHotspot = computed(() => {
  return props.data?.fields?.invisibleHotspot
})

const computedBorderFrame = computed(() => {
  return props.data?.fields?.spotBorderFrame
})

const computedBorderHeight = computed(() => {
  return props.data?.fields?.spotBorderHeight
})

const computedInstructionHeader = computed(() => {
  return props.data?.fields?.instructionHeader
})

const computedInstructionIcon = computed(() => {
  return props.data?.fields?.instructionIcon
})

const computedInstructionIntro = computed(() => {
  return props.data?.fields?.instructionIntro
})

const computedInstructionManual = computed(() => {
  return props.data?.fields?.instructionManual
})

const computedInstructionCta = computed(() => {
  return props.data?.fields?.instructionCta
})

const computedHotspots = computed(() => {
  return props.data?.fields?.hotspots || []
})

const computedHotspotInstructionIcon = computed(() => {
  return computedHotspots.value?.filter(instanceOfHotspot)?.[0]?.fields?.icon
    ?.fields?.file?.url
})

const shouldRenderCoordinates = computed(() => {
  return process.env.CTF_HOST === 'preview.contentful.com'
})

const computedHasInstruction = computed(
  () =>
    !!(
      props.data?.fields?.instructionIntro ||
      props.data?.fields?.instructionManual
    ),
)

const onHotSpotClick = (
  e: MouseEvent,
  hotspot: TypeHotspotWithoutUnresolvableLinksResponse,
) => {
  // this.destroyPopperInstance()
  isOpen.value = false
  isPopperVisible.value = false

  activeHotspot.value = hotspot

  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'hotspot_click',
    eventParams: {
      label: hotspot?.fields?.eventLabel,
      action: hotspot?.fields?.eventAction,
      category: hotspot?.fields?.eventCategory,
    },
  })

  if (hotspot?.fields?.image?.fields?.file?.url) {
    loadImage(hotspot?.fields?.image?.fields?.file?.url).then(() => {
      onRequestPopper(e, hotspot)
    })
  } else {
    onRequestPopper(e, hotspot)
  }
}

const getPopoverStyles = (
  hotspot: TypeHotspotWithoutUnresolvableLinksResponse,
) => {
  const { x = 0, y = 0 } = (hotspot?.fields?.coordinates ||
    {}) as PlacementCoordinates

  return {
    top: y,
    left: x,
  } as CSSProperties
}

const onRequestPopper = (
  e: MouseEvent,
  hotspot: TypeHotspotWithoutUnresolvableLinksResponse,
) => {
  requestAnimationFrame(() => {
    isOpen.value = true
    requestAnimationFrame(() => {
      if (e.target && detailRef.value) {
        popperInstance.value = createPopper(
          e.target as HTMLElement,
          detailRef.value,
          {
            placement: hotspot?.fields?.popperPlacement || 'bottom',
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [10, 20], // magic number from trial and error that fits most placement config
                },
              },
            ],
          },
        )
        requestAnimationFrame(() => {
          isPopperVisible.value = true
        })
      }
    })
  })
}

const onRequestDetailClose = () => {
  isOpen.value = false
  isPopperVisible.value = false
  activeHotspot.value = null
  destroyPopperInstance()
}

const destroyPopperInstance = () => {
  if (popperInstance.value) {
    popperInstance.value?.destroy()
    popperInstance.value = null
  }
}

const toggleCoordinates = () => {
  showCoordinates.value = !showCoordinates.value
}

const onRequestDismissInstruction = () => {
  shouldShowInstruction.value = false
  setTimeout(() => {
    isInstructionDismissed.value = true
  }, 800)
}

const onWindowResize = () => {
  if (panAreaRef.value) {
    panAreaRef.value.scrollLeft = (1024 - window.innerWidth) / 2 // recenter
  }
}

const onMouseDown = () => {
  isMouseDown.value = true
}

const onMouseUp = () => {
  isMouseDown.value = false
}

const onMouseMove = (e: MouseEvent) => {
  if (!isMouseDown.value || !isInstructionDismissed.value) return

  requestAnimationFrame(() => {
    if (panAreaRef.value) {
      panAreaRef.value.scrollLeft -= e.movementX
      panAreaRef.value.scrollTop -= e.movementY
    }
  })
}

onMounted(() => {
  if (!computedHasInstruction.value) {
    onRequestDismissInstruction()
  }

  onWindowResize()

  window.addEventListener('click', onRequestDetailClose)
  window.addEventListener('resize', onWindowResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('click', onRequestDetailClose)
  window.removeEventListener('resize', onWindowResize)
})

watch(isIntersecting, (newIsIntersecting) => {
  console.log('newIsIntersecting', newIsIntersecting)
  if (newIsIntersecting && computedHasInstruction.value) {
    shouldShowInstruction.value = true
  }
})
</script>

<style lang="scss" module>
.config {
  --track-area-min-width: 1024px;
  --wrap-min-height: 420px;
  --heading-color: var(--black);

  --hotspot-opacity--base: 1;
  --hotspot-opacity--sm: 1;
  --hotspot-opacity--md: 1;
  --hotspot-opacity: var(--hotspot-opacity--base);

  --detail-max-width--base: 300px;
  --detail-max-width--sm: 420px;
  --detail-max-width: var(--detail-max-width--base);

  --detail-max-height--base: calc(50vh - var(--site-header-height));
  --detail-max-height--sm: unset;
  --detail-max-height: var(--detail-max-height--base);

  @include media('sm') {
    --hotspot-opacity: var(--hotspot-opacity--sm);
    --detail-max-width: var(--detail-max-width--sm);
    --detail-max-height: var(--detail-max-height--sm);
  }

  @include media('md') {
    --hotspot-opacity: var(--hotspot-opacity--md);
  }
}

.block {
  position: relative;
}

.panArea {
  display: grid;
  width: 100%;
  max-width: var(--max-site-width);
  min-height: var(--wrap-min-height);
  overflow: auto;

  &.hideOverflow {
    overflow: hidden;
  }

  * {
    user-select: none;
  }
}

.heading.heading {
  color: var(--heading-color);
  text-align: center;
  line-height: 1;
  @include fluid-type(24px, 42px);

  width: var(--heading-width, 90vw);
  max-width: var(--heading-max-width, 720px);

  position: absolute;
  transform: translate(-50%, -100%);
}

.trackArea {
  display: inline-block; // so that it expands to backdrop's width
  position: relative;
  width: 100%;
  min-width: var(--track-area-min-width);
  backface-visibility: hidden;
}

.backdropPicture {
  display: block;
}

.backdrop {
  user-select: none;
  pointer-events: none;
  display: block;
  width: 100%;
}

.position {
  background-color: var(--black-alpha50);
  backdrop-filter: blur(3px);
  border-radius: 6px;

  position: absolute;
  padding: 1em;
  transform: translate(6px, 6px);
}

.popup {
  position: absolute;
  top: 0; // required to remove a slight jump on PC
  left: 0; // required to remove a slight jump on PC
}

.hotspotButton.hotspotButton {
  border: 0;
  padding: 0;
  background: none;

  &:hover {
    background: none;
  }
}

.hotspot {
  @include fluid-type(30px, 50px);
  position: absolute;
  transform: translate(-50%, -50%);
  opacity: var(--hotspot-opacity);

  button {
    color: var(--hotspot-button-color, var(--purple));
    font-size: 1em;
    font-weight: bold;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: var(--hotspot-button-min-width, 1em);
    min-height: var(--hotspot-button-min-height, 1em);

    &:active {
      color: var(--white);
    }
  }
}

.sectionCta {
  position: absolute;
  transform: translateX(-50%);
}

.icon {
  .hotspot.isActive button & {
    background: var(--purple);
  }

  border-radius: 50%;
  width: 1em;
  height: 1em;

  padding: 1px; // border width
  pointer-events: none;
  position: relative;
  transition: 300ms ease;
  z-index: 1;

  &.static {
    background: none !important;
    border-radius: unset !important;
    transition: unset !important;
    padding: 0 !important;
  }

  &.hidden {
    opacity: 0 !important;
    visibility: hidden !important;
  }

  &::before,
  &::after {
    content: '';
    border-radius: 50%;
    display: block;
    position: absolute;
    opacity: 0.5;
    transition: 300ms ease;
    transform: scale(0);
    z-index: -1;
  }

  &::before {
    background-color: var(--purple);
    top: -0.16em;
    right: -0.16em;
    left: -0.16em;
    bottom: -0.16em;

    .hotspot.isActive button &,
    .hotspot button:hover &,
    .hotspot button:focus & {
      transform: scale(1);
    }
  }

  &::after {
    background-color: var(--white);
    transform: scale(1);
    top: -0.2em;
    right: -0.2em;
    left: -0.2em;
    bottom: -0.2em;

    .hotspot.isActive button &,
    .hotspot button:hover &,
    .hotspot button:focus & {
      transform: scale(0);
    }
  }

  &.static::before,
  &.static::after {
    display: none;
  }
}

.iconInner {
  .hotspot.isActive button & {
    background: var(--purple);
    color: var(--white);
  }

  background: var(--white);
  border-radius: 50%;
  border: 2px solid var(--purple);

  color: var(--purple);
  transition: 300ms ease;

  display: grid;
  place-content: center;
  height: 100%;
  width: 100%;

  .static & {
    background: none !important;
    border: none !important;
  }
}

.label {
  font-size: 0.32em;
  padding: 0.66em;
  text-shadow: 0 3px 10px var(--black-alpha50);
  pointer-events: none;

  position: absolute;
  top: 100%;
  left: 50%;

  transition: 250ms ease;
  opacity: 1;
  transform: translate3d(-50%, 0, 0);

  .isActive & {
    opacity: 0;
    transform: translate3d(-50%, -8px, 0);
  }
}

.hiddenButton {
  --button-font-size: 12px !important;

  display: inline-block;
  position: absolute;
  left: 0;
  top: 0;

  margin: 1em;

  opacity: 0;
  transition: 300ms ease;

  &:hover {
    opacity: 1;
  }
}

.instruction {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--instruction-overlay, #{rgba(#000, 0.5)});
  display: grid;
  place-content: center;
  pointer-events: none;
  opacity: 0;
  transition: opacity 500ms ease;

  &.isPopperVisible {
    opacity: 1;
    pointer-events: auto;
  }
}

.instructionContent {
  border: var(--instruction-border);
  background: var(--instruction-background, #fff);

  @include fluid-type(16px, 18px);
  color: var(--instruction-text-color);
  width: 90vw;
  max-width: 34em;
  padding: 2.5em 2em;
  position: relative;
  opacity: 0;
  transition: 800ms ease;
  transform: translate3d(0, 60px, 0);
  will-change: transform;

  .isPopperVisible & {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

.instructionHeader {
  font-size: 1em;
  text-align: center;

  transition: 800ms ease;
  opacity: 0;
  transform: translate3d(0, 60px, 0);
  will-change: transform;

  .isPopperVisible & {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: var(--black);
    margin: 12px 0 0;
  }

  h6 {
    font-size: 1.5em;
  }
}

.instructionHeaderIcon {
  width: 3.75em;
}

.instructionIcon {
  flex: 0 0 auto;
  font-size: 40px;
  margin: 0 0.5em;
}

.instructionIntro {
  text-align: center;
  padding: 18px 0 30px;
  transition: 1000ms ease;

  opacity: 0;
  transform: translate3d(0, 60px, 0);
  will-change: transform;

  .isPopperVisible & {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }

  .isPopperVisible & {
    opacity: 1;
  }
}

.instructionManual {
  display: flex;
  padding: 15px;
  position: relative;
  border: 1px solid var(--gray-5);
  font-size: to-rem(15px);
  margin-bottom: 1.5em;

  transition: 1200ms ease;

  opacity: 0;
  transform: translate3d(0, 60px, 0);
  will-change: transform;

  .isPopperVisible & {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

.instructionCta {
  color: var(--base-font-color);

  transition: 1400ms ease;

  opacity: 0;
  transform: translate3d(0, 60px, 0);
  will-change: transform;

  .isPopperVisible & {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

.decor {
  @include fluid-type(60px, 90px);
  position: absolute;
  width: 1em;

  &.panelDecorTopLeft {
    top: 0;
    left: 0;
  }
  &.panelDecorTopRight {
    top: 0;
    right: 0;
  }
  &.panelDecorBottomLeft {
    bottom: 0;
    left: 0;
  }
  &.panelDecorBottomRight {
    bottom: 0;
    right: 0;
  }
}
</style>
